/**
 * DV-002/DV-003: Test Block Component
 * Reusable container for demo test components with consistent styling and timing
 */

import React, { ReactNode, useState, useCallback } from 'react';
import { ChevronDown, CheckCircle, AlertTriangle, XCircle, Info } from 'lucide-react';
import { cn } from '@/utils/cn';

export type TestStatus = 'success' | 'warning' | 'error' | 'info' | 'idle';

export interface TestBlockProps {
  title: string;
  description?: string;
  status?: TestStatus;
  actions?: ReactNode;
  children: ReactNode;
  defaultOpen?: boolean;
  className?: string;
  'data-testid'?: string;
  onRun?: (fn: () => void | Promise<void>) => Promise<void>;
}

const StatusIcon = ({ status }: { status: TestStatus }) => {
  const iconStyle = { width: '16px', height: '16px' };

  switch (status) {
    case 'success':
      return <CheckCircle style={{ ...iconStyle, color: 'var(--success)' }} />;
    case 'warning':
      return <AlertTriangle style={{ ...iconStyle, color: 'var(--warning)' }} />;
    case 'error':
      return <XCircle style={{ ...iconStyle, color: 'var(--danger)' }} />;
    case 'info':
      return <Info style={{ ...iconStyle, color: 'var(--accent)' }} />;
    default:
      return null;
  }
};

const getStatusBorderColor = (status: TestStatus): string => {
  switch (status) {
    case 'success':
      return 'var(--success)';
    case 'warning':
      return 'var(--warning)';
    case 'error':
      return 'var(--danger)';
    case 'info':
      return 'var(--accent)';
    default:
      return 'var(--border-default)';
  }
};

export function TestBlock({
  title,
  description,
  status = 'idle',
  actions,
  children,
  defaultOpen = true,
  className,
  'data-testid': testId = 'test-block',
  onRun
}: TestBlockProps) {
  const [isExpanded, setIsExpanded] = useState(defaultOpen);

  // Create a timing wrapper for test runs
  const createTimedAction = useCallback((originalAction: () => void | Promise<void>) => {
    return async () => {
      if (onRun) {
        await onRun(originalAction);
      } else {
        await originalAction();
      }
    };
  }, [onRun]);

  // Clone actions to wrap them with timing if onRun is provided
  const enhancedActions = useCallback(() => {
    if (!actions || !onRun) {
      return actions;
    }

    // If actions is a React element with onClick handlers, wrap them
    if (React.isValidElement(actions)) {
      return React.cloneElement(actions as React.ReactElement<any>, {
        children: React.Children.map((actions as React.ReactElement).props.children, (child: any) => {
          if (React.isValidElement(child)) {
            const childProps = child.props as any;
            if (childProps && typeof childProps.onClick === 'function') {
              return React.cloneElement(child as React.ReactElement<any>, {
                onClick: createTimedAction(childProps.onClick)
              });
            }
          }
          return child;
        })
      });
    }

    return actions;
  }, [actions, onRun, createTimedAction]);

  return (
    <div
      className={cn("rounded-lg border overflow-hidden", className)}
      style={{
        backgroundColor: 'var(--bg-surface)',
        borderColor: getStatusBorderColor(status),
        borderWidth: status !== 'idle' ? '2px' : '1px',
      }}
      data-testid={testId}
    >
      {/* Header */}
      <div
        className="px-4 py-3 border-b"
        style={{
          backgroundColor: 'var(--bg-elevated)',
          borderColor: 'var(--border-subtle)',
        }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center space-x-2 focus:outline-none focus:ring-2 rounded"
              style={{
                ['--focus-ring' as any]: '0 0 0 2px var(--border-focus)',
              }}
              onFocus={(e) => {
                e.currentTarget.style.boxShadow = 'var(--focus-ring)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.boxShadow = 'none';
              }}
              data-testid={`${testId}-toggle`}
            >
              <ChevronDown
                className={cn("w-4 h-4 transition-transform", !isExpanded && "-rotate-90")}
                style={{
                  transitionDuration: 'var(--motion-duration-fast)',
                  color: 'var(--fg-muted)'
                }}
              />
              <h3
                className="font-mono text-sm font-medium truncate"
                style={{
                  fontFamily: 'var(--font-family-mono)',
                  color: 'var(--fg-primary)'
                }}
              >
                {title}
              </h3>
            </button>

            {status !== 'idle' && <StatusIcon status={status} />}

            {description && (
              <p
                className="text-xs truncate"
                style={{ color: 'var(--fg-muted)' }}
                title={description}
              >
                {description}
              </p>
            )}
          </div>

          {actions && (
            <div className="flex items-center space-x-2 ml-4">
              {enhancedActions()}
            </div>
          )}
        </div>
      </div>

      {/* Content Body */}
      {isExpanded && (
        <div
          className="p-4"
          style={{
            backgroundColor: 'var(--bg-surface)',
            // Add subtle animation
            animation: 'fadeIn var(--motion-duration-fast) var(--motion-ease-standard)',
          }}
          data-testid={`${testId}-content`}
        >
          {children}
        </div>
      )}
    </div>
  );
}